import { AlertTriangle } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import useProjectStore from '@/store/useProjectStore'
import useUserStore from '@/store/useUserStore'

/**
 * A modal dialog that displays when a row version conflict occurs during data updates.
 * This component automatically watches for version conflict state in both useProjectStore
 * and useUserStore, and displays a warning when either store has a conflict.
 *
 * The modal is controlled by the `versionConflict` state in Zustand stores and is dismissed
 * by calling the appropriate `clearVersionConflict()` action.
 *
 * @example
 * // In App.jsx or a layout component, simply render the modal:
 * import { VersionConflictModal } from '@/components/common/VersionConflictModal'
 *
 * function App() {
 *   return (
 *     <>
 *       <Routes>...</Routes>
 *       <VersionConflictModal />
 *     </>
 *   )
 * }
 *
 * @example
 * // In a store, set version conflict when API returns 409:
 * try {
 *   await api.updateProject(id, updates)
 * } catch (error) {
 *   if (error.status === 409) {
 *     set({ versionConflict: { type: 'project', id } })
 *   }
 * }
 */
export function VersionConflictModal() {
  const projectVersionConflict = useProjectStore((state) => state.versionConflict)
  const clearProjectVersionConflict = useProjectStore((state) => state.clearVersionConflict)

  const userVersionConflict = useUserStore((state) => state.versionConflict)
  const clearUserVersionConflict = useUserStore((state) => state.clearVersionConflict)

  // Determine which conflict is active (project takes precedence if both exist)
  const hasConflict = projectVersionConflict !== null || userVersionConflict !== null
  const activeConflict = projectVersionConflict || userVersionConflict

  const handleClose = () => {
    if (projectVersionConflict !== null) {
      clearProjectVersionConflict()
    }
    if (userVersionConflict !== null) {
      clearUserVersionConflict()
    }
  }

  return (
    <AlertDialog open={hasConflict} onOpenChange={(open) => !open && handleClose()}>
      <AlertDialogContent data-testid="version-conflict-modal">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center border-2 border-amber-500 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-lg font-semibold">
              Update Conflict
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 text-sm text-gray-600">
            This record was modified by someone else. Your changes were not saved. Please review and try again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={handleClose}
            className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
            data-testid="version-conflict-ok-button"
          >
            Got it
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
